import { useRouter, useNavigate, createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { AdminGuard } from "@/components/shared/admin-guard";
import {
  useGetProfile,
  usePendingUsers,
  useApproveUser,
  useRejectUser,
} from "@/lib/api/auth/hooks";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import type { PendingUser } from "@/lib/api/auth/types";

// 테스트 전화번호 패턴 (0100000xxxx)
const TEST_PHONE_NUMBER_PATTERN = /^0100000\d{4}$/;

const filterOutTestUsers = (users: PendingUser[]) =>
  users.filter((user) => !TEST_PHONE_NUMBER_PATTERN.test(user.phoneNumber));

function PermissionPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const { data: user } = useGetProfile();
  const { data: pendingUsers = [], isLoading } = usePendingUsers();
  const approveUser = useApproveUser();
  const rejectUser = useRejectUser();
  const { setAlertDialogState, resetAlertDialogState } =
    useAlertDialogStore();

  useLoadingIndicator(isLoading);

  // 미승인 사용자 리다이렉트
  useEffect(() => {
    if (user && !user.isApproved) {
      navigate({ to: "/pending-approval", replace: true });
    }
  }, [user, router]);

  const filteredStudents = useMemo(
    () => filterOutTestUsers(pendingUsers).filter((u) => u.role === "student"),
    [pendingUsers]
  );

  const filteredTeachers = useMemo(
    () => filterOutTestUsers(pendingUsers).filter((u) => u.role === "teacher"),
    [pendingUsers]
  );

  const handleApprove = async (userId: string, role: string) => {
    try {
      await approveUser.mutateAsync(userId);
      const roleText = role === "teacher" ? "선생님" : "학생";
      toast.success(`${roleText} 승인이 완료되었습니다.`);
    } catch {
      toast.error("승인 처리에 실패했습니다.");
    }
  };

  const handleReject = (userId: string, name: string, role: string) => {
    const roleText = role === "teacher" ? "선생님" : "학생";
    setAlertDialogState({
      title: "승인 거부",
      description: `"${name}" ${roleText}의 가입을 거부하시겠습니까? 거부 시 계정이 삭제됩니다.`,
      confirmText: "거부",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await rejectUser.mutateAsync(userId);
          toast.success("거부되었습니다.");
        } catch {
          toast.error("거부 처리에 실패했습니다.");
        }
      },
      onCancel: () => resetAlertDialogState(),
    });
  };

  if (!user) return null;

  return (
    <AdminGuard>
      <PageContainer>
        <Header
          title="권한 관리"
          description={`${user.name}님 (${
            user.role === "admin" ? "관리자" : "선생님"
          })`}
        />

        {/* 승인 대기 사용자 목록 (Tabs로 구분) */}
        <div className="bg-card rounded-lg border">
          <Tabs defaultValue="students" className="w-full">
            <TabsList className="grid grid-cols-2 m-4">
              <TabsTrigger value="students">
                학생 ({filteredStudents.length})
              </TabsTrigger>
              <TabsTrigger value="teachers">
                선생님 ({filteredTeachers.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="students" className="px-4 pb-4">
              {filteredStudents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  승인 대기 중인 학생이 없습니다.
                </div>
              ) : (
                <div className="divide-y">
                  {filteredStudents.map((pendingUser) => (
                    <div
                      key={pendingUser._id}
                      className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold">{pendingUser.name}</h3>
                          <span className="text-sm text-muted-foreground">
                            {pendingUser.userId}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {pendingUser.phoneNumber} · {pendingUser.birthDate}
                          {pendingUser.school && ` · ${pendingUser.school}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="승인"
                          onClick={() =>
                            handleApprove(pendingUser.userId, pendingUser.role)
                          }
                        >
                          <Check className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="거부"
                          onClick={() =>
                            handleReject(
                              pendingUser.userId,
                              pendingUser.name,
                              pendingUser.role
                            )
                          }
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="teachers" className="px-4 pb-4">
              {filteredTeachers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  승인 대기 중인 선생님이 없습니다.
                </div>
              ) : (
                <div className="divide-y">
                  {filteredTeachers.map((pendingUser) => (
                    <div
                      key={pendingUser._id}
                      className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold">{pendingUser.name}</h3>
                          <span className="text-sm text-muted-foreground">
                            {pendingUser.userId}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {pendingUser.phoneNumber} · {pendingUser.birthDate}
                          {pendingUser.school && ` · ${pendingUser.school}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="승인"
                          onClick={() =>
                            handleApprove(pendingUser.userId, pendingUser.role)
                          }
                        >
                          <Check className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="거부"
                          onClick={() =>
                            handleReject(
                              pendingUser.userId,
                              pendingUser.name,
                              pendingUser.role
                            )
                          }
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </PageContainer>
    </AdminGuard>
  );
}

export const Route = createFileRoute("/_content/permission")({
  component: PermissionPage,
});
